import { useMemo } from "react";
import MonetizationOnOutlined from "@mui/icons-material/MonetizationOnOutlined";
import { format, parseISO } from "date-fns";
import { MetricCard } from "@/components/common/MetricCards";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { useEndorsements } from "@/resources/policies/hooks/useEndorsements";
import { useAccommodationRuns } from "./useAccommodationRuns";

interface AggregateAccommodationSummaryProps {
  policyId: string;
}

const EMPTY = "—";

export const AggregateAccommodationSummary = ({ policyId }: AggregateAccommodationSummaryProps) => {
  const { getByCategory } = useEndorsements(policyId);
  const hasAggregateAccommodation = getByCategory("aggregate_accommodation").some((e) => e.is_currently_active);

  const { runs, isLoading } = useAccommodationRuns(policyId);

  // Latest non-superseded (any run_type) — mirrors AccommodationTable.tsx
  // pattern. Used for the subtitle and the per-month metrics
  // (prorated_attachment, spend_over_deductible) that are populated on both
  // monthly and reconciliation rows.
  const latest = useMemo(() => {
    const sorted = [...runs].sort((a, b) => new Date(b.run_date).getTime() - new Date(a.run_date).getTime());
    return sorted.find((r) => !r.is_superseded);
  }, [runs]);

  // Story #4635: a separate "latest *monthly* run" lookup so the new "Latest
  // Month Funded" sub-metric doesn't leak the reconciliation row's funding
  // amount (which is `additional_reimbursement` per the backend and belongs
  // to a different ledger than monthly advances).
  const latestMonthly = useMemo(() => {
    const sorted = [...runs].sort((a, b) => new Date(b.run_date).getTime() - new Date(a.run_date).getTime());
    return sorted.find((r) => !r.is_superseded && r.run_type === "monthly");
  }, [runs]);

  // Story #4635: cumulative YTD funded across non-superseded monthly runs.
  // Becomes the tile's headline so the at-a-glance number reflects "how much
  // has been advanced this year" rather than just the most recent month
  // (which is often $0 and looks like the policy has no activity).
  // Reconciliation runs excluded — their funding_amount belongs to a
  // different ledger. Matches the same compute used by AccommodationTable's
  // year-end banner.
  //
  // Single intermediate memo holds the filtered slice so the headline value,
  // the sum, and the "has any" check all share one pass over `runs`. Note:
  // `totalMonthlyFunded > 0` is *not* a valid substitute for the empty-list
  // check — a policy with active monthly runs all at $0 is distinct from a
  // policy with no monthly runs at all (former shows $0.00, latter shows —).
  const nonSupersededMonthlyRuns = useMemo(
    () => runs.filter((r) => r.run_type === "monthly" && !r.is_superseded),
    [runs]
  );
  const totalMonthlyFunded = useMemo(
    () => nonSupersededMonthlyRuns.reduce((sum, r) => sum + r.funding_amount, 0),
    [nonSupersededMonthlyRuns]
  );
  const hasAnyMonthlyRun = nonSupersededMonthlyRuns.length > 0;

  // Endorsement gate: hide entirely when no active aggregate_accommodation endorsement.
  if (!hasAggregateAccommodation) return null;

  const subtitle = latest
    ? `${format(parseISO(latest.run_date), "MMM yyyy")} · ${latest.months_elapsed} of ${latest.policy_months}`
    : undefined;

  // Story #4635: headline = cumulative YTD; preserve the prior latest-month
  // value as a sub-metric so the recent-activity signal isn't lost.
  const headline = hasAnyMonthlyRun ? formatUSD(totalMonthlyFunded) : EMPTY;
  const latestMonthFunded = latestMonthly ? formatUSD(latestMonthly.funding_amount) : EMPTY;

  // spend_over_deductible is signed from v_policy_accommodation_summary
  // (ADR-033). A negative value means the policy is *under* the prorated
  // attachment point by that amount — render $0 in the sub-metric slot to
  // keep the label honest, and surface the headroom in a caption so the
  // signal isn't lost.
  const spendOverValue = latest
    ? latest.spend_over_deductible > 0
      ? formatUSD(latest.spend_over_deductible)
      : "$0"
    : EMPTY;
  const spendOverCaption =
    latest && latest.spend_over_deductible < 0
      ? `${formatUSD(Math.abs(latest.spend_over_deductible))} under attachment point`
      : undefined;

  const subMetrics = [
    {
      label: "Prorated Attachment",
      value: latest ? formatUSD(latest.prorated_attachment) : EMPTY,
    },
    {
      label: "Spend Over Deductible",
      value: spendOverValue,
      caption: spendOverCaption,
    },
    {
      label: "Latest Month Funded",
      value: latestMonthFunded,
    },
  ];

  return (
    <MetricCard
      icon={<MonetizationOnOutlined sx={{ fontSize: 18, color: "primary.main" }} />}
      title="Aggregate Accommodation"
      value={headline}
      subtitle={subtitle}
      subMetrics={subMetrics}
      isLoading={isLoading}
    />
  );
};
